import { createContext, useContext, useEffect, useMemo } from 'react'
import type { ReactNode } from 'react'
import type { TenantConfig } from './TenantConfig'
import { getTenantById } from './tenants'
import { useAuth } from '@/auth/AuthContext'

interface TenantContextValue {
  tenant: TenantConfig
}

const TenantContext = createContext<TenantContextValue | null>(null)

export function TenantProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth()
  const tenant = useMemo(() => {
    if (!session) return null
    return getTenantById(session.tenantId) ?? null
  }, [session])

  useEffect(() => {
    const root = document.documentElement
    if (!tenant) {
      root.removeAttribute('data-tenant')
      root.style.removeProperty('--brand-primary-override')
      root.style.removeProperty('--brand-accent-override')
      root.style.removeProperty('--brand-secondary-override')
      return
    }
    root.setAttribute('data-tenant', tenant.id)
    if (tenant.branding.primaryColor) {
      root.style.setProperty('--brand-primary', tenant.branding.primaryColor)
    }
    if (tenant.branding.accentColor) {
      root.style.setProperty('--brand-accent', tenant.branding.accentColor)
    }
    if (tenant.branding.secondaryColor) {
      root.style.setProperty('--brand-secondary', tenant.branding.secondaryColor)
    }
    document.title = `${tenant.name} · Dashboard`
    return () => {
      root.style.removeProperty('--brand-primary')
      root.style.removeProperty('--brand-accent')
      root.style.removeProperty('--brand-secondary')
    }
  }, [tenant])

  if (!tenant) return null

  const value: TenantContextValue = { tenant }
  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
}

export function useTenant(): TenantConfig {
  const ctx = useContext(TenantContext)
  if (!ctx) throw new Error('useTenant must be used within <TenantProvider>')
  return ctx.tenant
}
